// ============================================================
// LinuxFileSystem.jsx
//
// Branded, responsive course content page for the
// "Linux File System Structure" lesson.
//
// Brand palette (matches LinuxIntro.jsx & LinuxFirewall.jsx):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (bright gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl breakpoints)
//   - Heavily commented for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import FolderIcon              from "@mui/icons-material/Folder";
import FolderOpenIcon          from "@mui/icons-material/FolderOpen";
import FolderSpecialIcon       from "@mui/icons-material/FolderSpecial";
import HomeIcon                from "@mui/icons-material/Home";
import SettingsIcon            from "@mui/icons-material/Settings";
import TerminalIcon            from "@mui/icons-material/Terminal";
import StorageIcon             from "@mui/icons-material/Storage";
import DevicesIcon             from "@mui/icons-material/Devices";
import MemoryIcon              from "@mui/icons-material/Memory";
import BuildIcon               from "@mui/icons-material/Build";
import AccountTreeIcon         from "@mui/icons-material/AccountTree";
import SearchIcon              from "@mui/icons-material/Search";
import LinkIcon                from "@mui/icons-material/Link";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ContentCopyIcon         from "@mui/icons-material/ContentCopy";
import DoneIcon                from "@mui/icons-material/Done";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import StarIcon                from "@mui/icons-material/Star";
import SchoolIcon              from "@mui/icons-material/School";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";
import LayersIcon              from "@mui/icons-material/Layers";
import DnsIcon                 from "@mui/icons-material/Dns";
import ArticleIcon             from "@mui/icons-material/Article";
import SwapHorizIcon           from "@mui/icons-material/SwapHoriz";

// ── Brand tokens ──────────────────────────────────────────
// All colours in one place — change here to retheme everything.
const BRAND = {
    bg:           "#233D4D",
    surface:      "rgba(255,255,255,0.04)",
    border:       "rgba(218,165,32,0.25)",
    borderHover:  "rgba(218,165,32,0.6)",
    gold:         "#DAA520",
    goldBright:   "#FFD700",
    goldGlow:     "rgba(218,165,32,0.15)",
    white:        "#FFFFFF",
    muted:        "rgba(255,255,255,0.55)",
    codeBg:       "rgba(0,0,0,0.45)",
    codeText:     "#FFD700",
    danger:       "rgba(255,80,80,0.12)",
    dangerBorder: "rgba(255,80,80,0.35)",
    dangerText:   "#ff8080",
    success:      "rgba(39,201,63,0.10)",
    successBorder:"rgba(39,201,63,0.35)",
    successText:  "#27c93f",
    // Directory type colours — used in the directory tree
    dirSystem:    "#ff8c69",   // salmon  – system-critical dirs
    dirUser:      "#64c8ff",   // blue    – user-facing dirs
    dirConfig:    "#a78bfa",   // violet  – configuration dirs
    dirVirtual:   "#34d399",   // green   – virtual / pseudo dirs
    dirTemp:      "#fbbf24",   // amber   – temporary dirs
};

// ── Shared heading style ──────────────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ============================================================
//  SUB-COMPONENT: SectionTitle
//  Gold-icon circle + section heading. Reused by every section.
//  Props:
//    icon  – MUI icon element
//    label – heading text
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
//  Hoverable card explaining one file-system concept.
//  Props:
//    icon        – MUI icon element
//    title       – concept name
//    description – plain-English explanation (1–2 sentences)
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 20px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography
                    sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}
                >
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Terminal block with copy-to-clipboard functionality.
//  Props:
//    lines – array of { cmd: string, comment?: string }
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);

    // Concatenate all commands for the clipboard
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
            }}
        >
            {/* ── Terminal title bar ── */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${BRAND.border}`,
                    background: "rgba(0,0,0,0.3)",
                }}
            >
                {/* macOS-style decorative dots */}
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>

                {/* Copy button with tick confirmation */}
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box
                        onClick={handleCopy}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: copied ? BRAND.goldBright : BRAND.muted,
                            fontSize: 12,
                            transition: "color 0.2s",
                            "&:hover": { color: BRAND.gold },
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>

            {/* ── Command lines ── */}
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {/* Optional grey comment line above the command */}
                        {comment && (
                            <Typography
                                sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}
                            >
                                # {comment}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                fontFamily: "monospace",
                                fontSize: { xs: 12, md: 13 },
                                color: BRAND.codeText,
                                wordBreak: "break-all",
                            }}
                        >
                            {cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: DirectoryRow
//  One row in the file-system hierarchy table.
//  Shows the path, a colour-coded type badge, and description.
//  Props:
//    path        – directory path string e.g. "/etc"
//    type        – category: "system" | "user" | "config" | "virtual" | "temp"
//    label       – one-line description
//    icon        – MUI icon element
// ============================================================
function DirectoryRow({ path, type, label, icon }) {
    // Map type → colour token
    const typeColor = {
        system:  BRAND.dirSystem,
        user:    BRAND.dirUser,
        config:  BRAND.dirConfig,
        virtual: BRAND.dirVirtual,
        temp:    BRAND.dirTemp,
    }[type] || BRAND.gold;

    // Human-readable type label
    const typeLabel = {
        system:  "System",
        user:    "User",
        config:  "Config",
        virtual: "Virtual",
        temp:    "Temp",
    }[type] || type;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: { xs: 1, sm: 1.5 },
                py: 1.4,
                px: 1.5,
                borderBottom: `1px solid ${BRAND.border}`,
                "&:last-child": { borderBottom: "none" },
                transition: "background 0.2s",
                "&:hover": { background: "rgba(255,255,255,0.02)" },
            }}
        >
            {/* Folder icon in type colour */}
            {React.cloneElement(icon, { sx: { color: typeColor, fontSize: 20, flexShrink: 0 } })}

            {/* Directory path in gold monospace */}
            <Typography
                sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: 13, md: 14 },
                    fontWeight: 700,
                    color: BRAND.goldBright,
                    minWidth: { xs: 80, sm: 120 },
                    flexShrink: 0,
                }}
            >
                {path}
            </Typography>

            {/* Type chip — colour-coded category badge */}
            <Chip
                label={typeLabel}
                size="small"
                sx={{
                    background: `${typeColor}18`,
                    border: `1px solid ${typeColor}55`,
                    color: typeColor,
                    fontFamily: "monospace",
                    fontSize: 10,
                    height: 20,
                    letterSpacing: 0.5,
                    flexShrink: 0,
                }}
            />

            {/* Plain-English description */}
            <Typography
                sx={{
                    fontSize: 13,
                    color: BRAND.muted,
                    flex: 1,
                    minWidth: 0,
                    lineHeight: 1.5,
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: CommandRow
//  A single command + description row for the "Useful Commands"
//  reference table.
//  Props:
//    cmd         – the shell command string
//    description – what it does
// ============================================================
function CommandRow({ cmd, description }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: { xs: 1, sm: 2 },
                py: 1.4,
                px: 1.5,
                borderBottom: `1px solid ${BRAND.border}`,
                "&:last-child": { borderBottom: "none" },
                transition: "background 0.2s",
                "&:hover": { background: "rgba(255,255,255,0.02)" },
            }}
        >
            {/* Command in gold monospace — min-width keeps columns tidy */}
            <Typography
                sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: 12, md: 13 },
                    color: BRAND.codeText,
                    fontWeight: 600,
                    minWidth: { xs: "100%", sm: 200 },
                    flexShrink: 0,
                    wordBreak: "break-all",
                }}
            >
                {cmd}
            </Typography>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, flex: 1, lineHeight: 1.6 }}>
                {description}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
//  Accordion-style card for optional deeper content.
//  Props:
//    title    – accordion header text
//    icon     – MUI icon element
//    children – JSX rendered inside when expanded
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`,
                borderRadius: 2.5,
                overflow: "hidden",
                mb: 1.5,
                transition: "border-color 0.25s",
            }}
        >
            {/* Clickable header */}
            <Box
                onClick={() => setOpen((p) => !p)}
                sx={{
                    px: 2.5,
                    py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: open ? BRAND.goldGlow : BRAND.surface,
                    transition: "background 0.25s",
                    "&:hover": { background: BRAND.goldGlow },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Box>
                {open
                    ? <ExpandLessIcon sx={{ color: BRAND.gold }} />
                    : <ExpandMoreIcon sx={{ color: BRAND.muted }} />
                }
            </Box>

            {/* Collapsible body — MUI Collapse gives a smooth animation */}
            <Collapse in={open}>
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        background: "rgba(0,0,0,0.25)",
                        borderTop: `1px solid ${BRAND.border}`,
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
//  Styled external-link row for the Resources section.
//  Props:
//    href  – full URL
//    label – display name
//    sub   – short description text
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                mb: 1.2,
                textDecoration: "none",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                background: BRAND.surface,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 16px ${BRAND.goldGlow}`,
                },
            }}
        >
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>
                        {sub}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxFileSystem
//  Full "Linux File System Structure" lesson page.
// ============================================================
export default function LinuxFileSystem() {
    return (
        // ── Page wrapper ─────────────────────────────────────
        <Box
            sx={{
                minHeight: "100vh",
                background: BRAND.bg,
                // Subtle diagonal texture — consistent with other lesson pages
                backgroundImage: `repeating-linear-gradient(
          -45deg,
          rgba(218,165,32,0.02) 0px,
          rgba(218,165,32,0.02) 1px,
          transparent 1px,
          transparent 12px
        )`,
                py: { xs: 4, md: 7 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>

                    {/* Module badge chip */}
                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Foundation · Module 02"
                        sx={{
                            mb: 2.5,
                            background: BRAND.goldGlow,
                            border: `1px solid ${BRAND.border}`,
                            color: BRAND.gold,
                            fontFamily: "monospace",
                            fontSize: 11,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                    />

                    {/* Page title with gold gradient on key word */}
                    <Typography
                        variant="h3"
                        sx={{
                            ...sectionHeading,
                            fontSize: { xs: 26, sm: 34, md: 42 },
                            mb: 2,
                            "& span": {
                                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            },
                        }}
                    >
                        Linux <span>File System</span> Structure
                    </Typography>

                    {/* Intro paragraph */}
                    <Typography
                        sx={{
                            color: BRAND.muted,
                            fontSize: { xs: 14, md: 16 },
                            maxWidth: 680,
                            lineHeight: 1.8,
                            mx: { xs: "auto", md: 0 },
                            mb: 3,
                        }}
                    >
                        The Linux file system is a single unified tree that starts at{" "}
                        <code style={{ color: BRAND.goldBright, fontSize: 14 }}>/</code> (the root directory).
                        Unlike Windows — which uses drive letters like <code style={{ color: BRAND.goldBright, fontSize: 14 }}>C:\</code> —
                        everything in Linux is mounted under this one root, including hard drives, USB sticks,
                        network shares, and even running processes. Understanding this hierarchy is foundational
                        to administering any Linux system.
                    </Typography>

                    {/* Quick-stat chips */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            justifyContent: { xs: "center", md: "flex-start" },
                        }}
                    >
                        {[
                            { label: "~2 hours",  sub: "read time"    },
                            { label: "Beginner",  sub: "level"         },
                            { label: "15+ dirs",  sub: "documented"    },
                        ].map(({ label, sub }) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    background: BRAND.surface,
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1.2,
                                }}
                            >
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 15 }}>
                                    {label}
                                </Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="Key Concepts" />

                    <Grid container spacing={2}>
                        {[
                            {
                                icon: <AccountTreeIcon />,
                                title: "Hierarchical Tree",
                                description:
                                    "Linux uses a single inverted tree starting at /. Every file, directory, device, and process lives somewhere in this tree. There are no drive letters — everything is a path.",
                            },
                            {
                                icon: <ArticleIcon />,
                                title: "Everything Is a File",
                                description:
                                    "Linux treats almost everything as a file — regular files, directories, hardware devices (/dev/sda), network sockets, and even running processes (/proc/1234). One consistent interface for all.",
                            },
                            {
                                icon: <LayersIcon />,
                                title: "Mount Points",
                                description:
                                    "In Linux, storage devices are not assigned letters. Instead they are 'mounted' at a directory path. A USB drive might appear at /media/usb or /mnt/usb — same tree, different branch.",
                            },
                            {
                                icon: <FolderSpecialIcon />,
                                title: "Filesystem Hierarchy Standard (FHS)",
                                description:
                                    "The FHS is a specification that defines what belongs where in a Linux file system. It ensures consistency across distributions — /etc always holds config, /bin always holds binaries.",
                            },
                            {
                                icon: <MemoryIcon />,
                                title: "Virtual File Systems",
                                description:
                                    "/proc and /sys are virtual — they don't exist on disk. They are generated by the kernel in real time and expose live information about processes, hardware, and kernel settings.",
                            },
                            {
                                icon: <SwapHorizIcon />,
                                title: "Symbolic & Hard Links",
                                description:
                                    "A symbolic link (symlink) is a pointer to another file, like a shortcut. A hard link is a second name for the same data on disk. Use ln -s to create symlinks; ln for hard links.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}>
                                <ConceptCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – DIRECTORY REFERENCE TABLE
            Colour-coded by category type for fast scanning.
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<FolderOpenIcon />} label="Directory Reference" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Every major directory has a defined purpose under the FHS. The colour of each
                        badge shows its category at a glance.
                    </Typography>

                    {/* Legend row explaining the colour coding */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
                        {[
                            { type: "system",  label: "System-critical", color: BRAND.dirSystem  },
                            { type: "user",    label: "User-facing",     color: BRAND.dirUser    },
                            { type: "config",  label: "Configuration",   color: BRAND.dirConfig  },
                            { type: "virtual", label: "Virtual (kernel)", color: BRAND.dirVirtual },
                            { type: "temp",    label: "Temporary",       color: BRAND.dirTemp    },
                        ].map(({ label, color }) => (
                            <Box
                                key={label}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.8,
                                    background: `${color}12`,
                                    border: `1px solid ${color}40`,
                                    borderRadius: 20,
                                    px: 1.5,
                                    py: 0.5,
                                }}
                            >
                                {/* Small colour dot */}
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 11, color, fontFamily: "monospace" }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Directory table */}
                    <Box
                        sx={{
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            overflow: "hidden",
                        }}
                    >
                        {/* Table header (hidden on xs — too narrow) */}
                        <Box
                            sx={{
                                display: { xs: "none", sm: "flex" },
                                gap: 1.5,
                                px: 1.5,
                                py: 1,
                                background: BRAND.goldGlow,
                                borderBottom: `1px solid ${BRAND.border}`,
                            }}
                        >
                            <Typography sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", width: 30 }}> </Typography>
                            <Typography sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", minWidth: 120 }}>Path</Typography>
                            <Typography sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", width: 80 }}>Type</Typography>
                            <Typography sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>Description</Typography>
                        </Box>

                        {/* One row per directory */}
                        {[
                            { path: "/",         type: "system",  icon: <FolderSpecialIcon />, label: "Root — the top of the entire file system tree. Everything lives under here." },
                            { path: "/bin",      type: "system",  icon: <BuildIcon />,         label: "Essential user command binaries (ls, cp, mv, cat). Available to all users, even in single-user mode." },
                            { path: "/sbin",     type: "system",  icon: <BuildIcon />,         label: "System binaries for administrators (fdisk, iptables, reboot). Normally only run as root." },
                            { path: "/lib",      type: "system",  icon: <LayersIcon />,        label: "Shared libraries needed by /bin and /sbin binaries — the Linux equivalent of .dll files." },
                            { path: "/boot",     type: "system",  icon: <DnsIcon />,           label: "Bootloader files including the Linux kernel (vmlinuz) and initial RAM disk (initrd)." },
                            { path: "/home",     type: "user",    icon: <HomeIcon />,          label: "Personal directories for each user. /home/alice holds alice's files, config, and Desktop." },
                            { path: "/root",     type: "user",    icon: <HomeIcon />,          label: "Home directory for the root (superuser) account. Separate from /home for security." },
                            { path: "/etc",      type: "config",  icon: <SettingsIcon />,      label: "System-wide configuration files. /etc/fstab, /etc/hosts, /etc/ssh/sshd_config all live here." },
                            { path: "/usr",      type: "user",    icon: <FolderIcon />,        label: "Secondary hierarchy for user-installed programs. /usr/bin, /usr/lib, /usr/share." },
                            { path: "/usr/bin",  type: "user",    icon: <BuildIcon />,         label: "Non-essential user commands installed by the package manager (python3, git, vim)." },
                            { path: "/usr/local",type: "user",    icon: <FolderOpenIcon />,    label: "Locally compiled or manually installed software — not managed by the package manager." },
                            { path: "/var",      type: "config",  icon: <ArticleIcon />,       label: "Variable data that changes at runtime: logs (/var/log), mail spools, print queues, databases." },
                            { path: "/var/log",  type: "config",  icon: <ArticleIcon />,       label: "System and application log files. syslog, auth.log, nginx/access.log all land here." },
                            { path: "/tmp",      type: "temp",    icon: <FolderIcon />,        label: "Temporary files. Automatically cleared on reboot. Never store anything important here." },
                            { path: "/dev",      type: "virtual", icon: <DevicesIcon />,       label: "Device files. /dev/sda is your first hard drive. /dev/null discards everything written to it." },
                            { path: "/proc",     type: "virtual", icon: <MemoryIcon />,        label: "Virtual filesystem exposing kernel and process info. /proc/cpuinfo, /proc/meminfo are live data." },
                            { path: "/sys",      type: "virtual", icon: <MemoryIcon />,        label: "Virtual filesystem exposing hardware and kernel state. Introduced to replace /proc for device info." },
                            { path: "/run",      type: "virtual", icon: <StorageIcon />,       label: "Runtime data since last boot — PID files, lock files, sockets. Stored in RAM (tmpfs)." },
                            { path: "/media",    type: "user",    icon: <StorageIcon />,       label: "Auto-mount point for removable media — USB drives, CD-ROMs, SD cards." },
                            { path: "/mnt",      type: "user",    icon: <StorageIcon />,       label: "Temporary mount point for manually mounted filesystems (NFS shares, extra disks)." },
                            { path: "/opt",      type: "user",    icon: <FolderOpenIcon />,    label: "Optional third-party software packages (e.g. /opt/google, /opt/lampp). Self-contained apps." },
                            { path: "/srv",      type: "config",  icon: <DnsIcon />,           label: "Data served by this machine — web files in /srv/www, FTP files in /srv/ftp." },
                        ].map((row) => (
                            <DirectoryRow key={row.path} {...row} />
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – TRY IT YOURSELF
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="Try It Yourself" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Open your terminal and run these commands. The goal is to build a mental map of
                        where things live. Don't memorise paths — learn to navigate and look things up.
                    </Typography>

                    {/* ── Exploring the tree ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Exploring the Tree
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ ls /",             comment: "List everything directly under root" },
                            { cmd: "$ tree -L 1 /",      comment: "Show the root tree one level deep (install: sudo apt install tree)" },
                            { cmd: "$ tree -L 2 /usr",   comment: "Explore /usr two levels deep" },
                            { cmd: "$ ls -la /etc",      comment: "List all config files with permissions and hidden files" },
                            { cmd: "$ ls -la /proc",     comment: "Peek inside the virtual process filesystem" },
                        ]}
                    />

                    {/* ── Disk & space ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Checking Disk Space
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ df -h",            comment: "Show disk usage of all mounted filesystems in human-readable units" },
                            { cmd: "$ df -hT",           comment: "Same but also shows the filesystem type (ext4, tmpfs, etc.)" },
                            { cmd: "$ du -sh /var/log",  comment: "Show how much space the /var/log directory is using" },
                            { cmd: "$ du -sh *",         comment: "Show sizes of everything in the current directory" },
                            { cmd: "$ du -sh /* 2>/dev/null | sort -rh | head -10", comment: "Find the 10 largest directories from root" },
                        ]}
                    />

                    {/* ── Mount points ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Working with Mount Points
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ mount",            comment: "List all currently mounted filesystems" },
                            { cmd: "$ mount | column -t",comment: "Same output but neatly aligned in columns" },
                            { cmd: "$ cat /etc/fstab",   comment: "View filesystems configured to mount automatically at boot" },
                            { cmd: "$ lsblk",            comment: "List all block devices and their mount points in a tree view" },
                            { cmd: "$ blkid",            comment: "Show UUIDs and types for all detected block devices" },
                        ]}
                    />

                    {/* ── Finding files ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Finding Files
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ find / -name 'sshd_config' 2>/dev/null",       comment: "Search the whole filesystem for a filename" },
                            { cmd: "$ find /etc -name '*.conf' -type f",              comment: "Find all .conf files inside /etc" },
                            { cmd: "$ locate nginx.conf",                             comment: "Fast indexed search (run: sudo updatedb first)" },
                            { cmd: "$ which python3",                                 comment: "Show the full path of an executable in $PATH" },
                            { cmd: "$ type ls",                                       comment: "Show whether a command is a binary, alias, or builtin" },
                        ]}
                    />

                    {/* ── Deeper expandable examples ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 4 }}>
                        🔽 Deeper Examples (click to expand)
                    </Typography>

                    {/* Accordion 1: /proc deep dive */}
                    <ExpandableExample title="Exploring /proc — the live kernel interface" icon={<MemoryIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            <code style={{ color: BRAND.goldBright }}>/proc</code> is one of the most powerful
                            directories in Linux. It does not exist on disk — the kernel generates it on the fly.
                            Each numbered subdirectory (e.g. <code style={{ color: BRAND.goldBright }}>/proc/1234</code>)
                            corresponds to a running process with that PID. Reading these files is how tools like{" "}
                            <code style={{ color: BRAND.goldBright }}>top</code> and{" "}
                            <code style={{ color: BRAND.goldBright }}>ps</code> get their data.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ cat /proc/cpuinfo",       comment: "Detailed CPU information — model, cores, flags" },
                                { cmd: "$ cat /proc/meminfo",       comment: "Detailed memory information — total, free, buffers" },
                                { cmd: "$ cat /proc/uptime",        comment: "How long the system has been running (seconds)" },
                                { cmd: "$ cat /proc/version",       comment: "Kernel version string" },
                                { cmd: "$ ls /proc/$$",             comment: "$$ is your current shell's PID — explore its directory" },
                                { cmd: "$ cat /proc/$$/status",     comment: "Status information about your current shell process" },
                                { cmd: "$ cat /proc/net/if_inet6",  comment: "IPv6 network interface information from the kernel" },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion 2: symlinks */}
                    <ExpandableExample title="Symbolic and hard links in practice" icon={<SwapHorizIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Links let one file appear in multiple locations. A{" "}
                            <strong style={{ color: BRAND.white }}>symlink</strong> points to a path (breaks if
                            the target moves). A <strong style={{ color: BRAND.white }}>hard link</strong> points
                            directly to the inode on disk (survives renames). Use{" "}
                            <code style={{ color: BRAND.goldBright }}>ls -la</code> to spot symlinks — they show
                            as <code style={{ color: BRAND.goldBright }}>lrwxrwxrwx</code> and display the target
                            after an arrow.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ ln -s /etc/nginx/nginx.conf ~/nginx.conf",  comment: "Create a symlink in home pointing to the nginx config" },
                                { cmd: "$ ls -la ~/nginx.conf",                       comment: "See the symlink arrow → target" },
                                { cmd: "$ ln /etc/hosts /tmp/hosts-copy",             comment: "Create a hard link (same inode, different path)" },
                                { cmd: "$ stat /etc/hosts",                           comment: "Inspect inode number and hard link count" },
                                { cmd: "$ readlink -f ~/nginx.conf",                  comment: "Resolve a symlink to its absolute real path" },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion 3: mounting a disk */}
                    <ExpandableExample title="Manually mounting a filesystem" icon={<StorageIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            When you plug in a USB drive or add a new disk, Linux doesn't automatically make it
                            accessible — you need to <strong style={{ color: BRAND.white }}>mount</strong> it to a
                            directory. The kernel then maps all file operations on that directory to the physical
                            device. To make a mount survive reboots, add it to{" "}
                            <code style={{ color: BRAND.goldBright }}>/etc/fstab</code>.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ lsblk",                              comment: "Identify the device name (e.g. /dev/sdb1)" },
                                { cmd: "$ sudo mkdir -p /mnt/usb",            comment: "Create a mount point directory" },
                                { cmd: "$ sudo mount /dev/sdb1 /mnt/usb",     comment: "Mount the device to our directory" },
                                { cmd: "$ ls /mnt/usb",                       comment: "The USB drive contents are now here" },
                                { cmd: "$ sudo umount /mnt/usb",              comment: "Safely unmount before physically removing" },
                                { cmd: "$ sudo mount -o remount,ro /mnt/usb", comment: "Remount read-only (useful for backups)" },
                            ]}
                        />
                        {/* Warning: never pull hardware without unmounting */}
                        <Box
                            sx={{
                                display: "flex", alignItems: "flex-start", gap: 1.5, p: 2, mt: 1,
                                background: BRAND.danger, border: `1px solid ${BRAND.dangerBorder}`, borderRadius: 2,
                            }}
                        >
                            <WarningAmberIcon sx={{ color: BRAND.dangerText, fontSize: 18, flexShrink: 0, mt: 0.1 }} />
                            <Typography sx={{ color: BRAND.dangerText, fontSize: 12, lineHeight: 1.6 }}>
                                Always run <code>sudo umount</code> before physically unplugging a drive.
                                Removing a mounted device can cause data corruption or filesystem errors.
                            </Typography>
                        </Box>
                    </ExpandableExample>

                    {/* Accordion 4: /etc/fstab */}
                    <ExpandableExample title="Understanding /etc/fstab — automatic mounts at boot" icon={<SettingsIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            <code style={{ color: BRAND.goldBright }}>/etc/fstab</code> is read at boot to
                            determine which filesystems to mount automatically. Each line has six fields:
                            device, mount point, filesystem type, options, dump, and pass. Using UUIDs instead
                            of device names (like <code style={{ color: BRAND.goldBright }}>/dev/sdb1</code>)
                            is safer because UUIDs never change when you add or remove disks.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ cat /etc/fstab",                            comment: "View current auto-mount configuration" },
                                { cmd: "$ sudo blkid",                                comment: "Get UUIDs for all block devices" },
                                { cmd: "$ sudo mount -a",                             comment: "Mount everything in fstab (test without rebooting)" },
                                { cmd: "$ sudo findmnt --verify",                     comment: "Validate fstab for errors before rebooting" },
                            ]}
                        />
                        <Typography sx={{ color: BRAND.muted, fontSize: 12, lineHeight: 1.7, mt: 1 }}>
                            Example fstab entry — add a line like this to auto-mount a disk by UUID:
                        </Typography>
                        <Box
                            sx={{
                                background: BRAND.codeBg,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                p: 2,
                                mt: 1,
                                fontFamily: "monospace",
                            }}
                        >
                            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.5 }}>
                                # UUID=... &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;/mount/point &nbsp;type &nbsp;options &nbsp;dump &nbsp;pass
                            </Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 11, md: 13 }, color: BRAND.codeText, wordBreak: "break-all" }}>
                                UUID=a1b2c3d4-e5f6-7890-abcd-ef1234567890 &nbsp;/mnt/data &nbsp;ext4 &nbsp;defaults,noatime &nbsp;0 &nbsp;2
                            </Typography>
                        </Box>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – USEFUL COMMANDS REFERENCE
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SearchIcon />} label="Useful Commands Reference" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        These are the file-system commands you will reach for every day. Bookmark this
                        table — you don't need to memorise them, just know they exist.
                    </Typography>

                    {/* Reference table */}
                    <Box
                        sx={{
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            overflow: "hidden",
                        }}
                    >
                        {/* Column headers */}
                        <Box
                            sx={{
                                display: { xs: "none", sm: "flex" },
                                gap: 2,
                                px: 1.5,
                                py: 1,
                                background: BRAND.goldGlow,
                                borderBottom: `1px solid ${BRAND.border}`,
                            }}
                        >
                            <Typography sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", minWidth: 200 }}>Command</Typography>
                            <Typography sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>Description</Typography>
                        </Box>

                        {[
                            { cmd: "ls -l /",                       description: "List root directory contents with permissions, owner, size, and timestamp." },
                            { cmd: "ls -la /etc",                   description: "List all files (including hidden dotfiles) in /etc with details." },
                            { cmd: "df -h",                         description: "Show disk usage for all mounted filesystems in human-readable sizes (KB/MB/GB)." },
                            { cmd: "df -hT",                        description: "Same as df -h but also shows the filesystem type (ext4, xfs, tmpfs, etc.)." },
                            { cmd: "du -sh /path",                  description: "Show the total disk space used by a specific directory." },
                            { cmd: "du -sh * | sort -rh",           description: "List sizes of everything in the current directory, sorted largest first." },
                            { cmd: "tree -L 2",                     description: "Show the directory tree 2 levels deep. Install with: sudo apt install tree." },
                            { cmd: "mount",                         description: "List all currently mounted filesystems with their options." },
                            { cmd: "lsblk",                         description: "Show all block devices (disks and partitions) in a tree view with mount points." },
                            { cmd: "blkid",                         description: "Show the UUID, label, and filesystem type of every block device." },
                            { cmd: "find / -name 'file.txt'",       description: "Search the entire filesystem for a file named file.txt." },
                            { cmd: "find /etc -type f -name '*.conf'", description: "Find all regular files matching *.conf inside /etc." },
                            { cmd: "locate filename",               description: "Fast indexed search. Much quicker than find — run sudo updatedb first." },
                            { cmd: "which python3",                 description: "Show the full absolute path of an executable found in $PATH." },
                            { cmd: "stat /etc/hosts",               description: "Show detailed metadata: size, inode, permissions, timestamps, hard link count." },
                            { cmd: "file /bin/ls",                  description: "Identify the type of a file (ELF binary, ASCII text, symlink, etc.)." },
                            { cmd: "readlink -f /path/to/symlink",  description: "Resolve a symlink chain and print the final absolute real path." },
                            { cmd: "ln -s /target /link",           description: "Create a symbolic link at /link pointing to /target." },
                        ].map((row) => (
                            <CommandRow key={row.cmd} {...row} />
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />

                    <Grid container spacing={1.5}>
                        {[
                            "Why Linux uses a single unified tree rooted at /",
                            "Why Linux treats devices, processes, and sockets as files",
                            "The purpose of every major directory from / to /srv",
                            "How mount points work instead of drive letters",
                            "How to explore the tree with ls, tree, and find",
                            "How to check disk space with df and du",
                            "How to view and understand mounted filesystems",
                            "The difference between symbolic links and hard links",
                            "How to read /proc to access live kernel information",
                            "How /etc/fstab controls automatic mounts at boot",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 6 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />

                    <ResourceLink
                        href="https://tldp.org/LDP/Linux-Filesystem-Hierarchy/html/"
                        label="Filesystem Hierarchy Standard (FHS) — TLDP"
                        sub="The official specification that defines what belongs in each directory. The authoritative reference."
                    />
                    <ResourceLink
                        href="https://linuxhandbook.com/linux-directory-structure/"
                        label="Linux Directory Structure Explained — Linux Handbook"
                        sub="Beginner-friendly walkthrough of every major directory with practical examples."
                    />
                    <ResourceLink
                        href="https://www.kernel.org/doc/html/latest/filesystems/proc.html"
                        label="The /proc Filesystem — Linux Kernel Docs"
                        sub="Official kernel documentation for every file and directory inside /proc."
                    />
                    <ResourceLink
                        href="https://man7.org/linux/man-pages/man5/fstab.5.html"
                        label="fstab(5) — Linux Man Page"
                        sub="Full man page for /etc/fstab including every field, option, and example."
                    />
                    <ResourceLink
                        href="https://www.baeldung.com/linux/symbolic-and-hard-links"
                        label="Symbolic vs Hard Links — Baeldung"
                        sub="Clear visual explanation of the difference between symlinks and hard links with diagrams."
                    />
                </Box>

                {/* ── Footer pro tip ── */}
                <Box
                    sx={{
                        mt: 2,
                        p: 2.5,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        background: BRAND.goldGlow,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                    }}
                >
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0, mt: 0.1 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> Run{" "}
                        <code style={{ color: BRAND.goldBright }}>man hier</code> in any terminal to read the
                        built-in Linux manual page that describes the file system hierarchy. It's always
                        available offline, even on a minimal server with no internet access. Pair it with{" "}
                        <code style={{ color: BRAND.goldBright }}>tree -L 2 /</code> to build a visual map
                        as you read.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
